import { useState } from 'react'

import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { renderErrorNotification } from 'store/notificationHandler/actions'
import {
  RESTORE_PANEL,
  RESTORE_PROFILE,
  TOGGLE_EDIT_MODE,
} from 'store/storefront/constants'
import { hasChanges, hasProfileChanges } from 'store/storefront/selectors'

import { Modal } from 'components/Core'

import styles from './StorefrontSaveChanges.module.css'
import {
  useUpsertStorefront,
  useUpsertStorefrontProfile,
} from 'features/Storefront/StorefrontNavbar/StorefrontNavbar.hooks'

const StorefrontSaveChanges = ({
  onClose,
  trackers = () => {},
  redirectTo,
}) => {
  const dispatch = useDispatch()
  const [isSaving, setIsSaving] = useState(false)
  const { loading, upsertStorefront } = useUpsertStorefront()
  const upsertStorefrontProfile = useUpsertStorefrontProfile()
  const navigate = useNavigate()
  const throwError = (text) => dispatch(renderErrorNotification(text))

  // The canvas and Brand Details are two independent drafts saved by two
  // different mutations, so each one is only touched when it is actually dirty.
  const panelHasChanges = useSelector(hasChanges)
  const profileHasChanges = useSelector(hasProfileChanges)

  const discardChanges = () => {
    if (panelHasChanges) {
      dispatch({ type: TOGGLE_EDIT_MODE })
      dispatch({ type: RESTORE_PANEL })
    }
    if (profileHasChanges) {
      dispatch({ type: RESTORE_PROFILE })
    }
  }

  const saveStorefront = async () => {
    try {
      const { data } = await upsertStorefront()
      if (data.errors) {
        throwError('There was an error saving your custom profile')
        return false
      }
      dispatch({ type: TOGGLE_EDIT_MODE })
      return true
    } catch {
      throwError('There was an error saving your custom profile')
      return false
    }
  }

  const saveProfile = async () => {
    try {
      const { data, errors } = await upsertStorefrontProfile()
      if (!data || errors) {
        throwError('There was an error saving your brand details')
        return false
      }
      return true
    } catch {
      throwError('There was an error saving your brand details')
      return false
    }
  }

  /**
   * Leaving the page tears down the requests that are still in flight, so the
   * redirect has to wait for every save to come back. A failed save keeps the
   * user here with their work rather than dropping it on the way out.
   */
  const saveAndLeave = async () => {
    setIsSaving(true)
    const saved = await Promise.all([
      panelHasChanges ? saveStorefront() : true,
      profileHasChanges ? saveProfile() : true,
    ])
    setIsSaving(false)
    trackers()
    if (saved.every(Boolean)) {
      onClose()
      navigate(redirectTo)
    }
  }

  const actionButtons = [
    {
      disabled: isSaving,
      type: 'primary',

      onClick: () => {},
      closeOnClick: true,
      label: 'Cancel',
      testId: '',
    },
    {
      disabled: isSaving,
      type: 'primary',
      onClick: () => {
        discardChanges()
        trackers()
        navigate(redirectTo)
      },
      closeOnClick: true,
      label: "Don't Save",
      testId: '',
    },
    {
      disabled: loading || isSaving,
      type: 'secondary',
      onClick: saveAndLeave,
      // The modal owns the redirect, so it also owns when to close: shutting it
      // on click would unmount the mutations it is waiting on.
      closeOnClick: false,
      label: 'Save',
      testId: '',
    },
  ]

  return (
    <Modal
      title={'Are you sure you want to leave this page?'}
      className={classNames(
        styles.StorefrontSaveChanges,
        styles.additionalClass,
      )}
      open
      onClose={onClose}
      large
      hideCloseButton
      actionButtons={actionButtons}
    >
      <div className={styles.title}>
        Leaving this page will discard any unsaved changes. Would you like to
        save changes before exiting?
      </div>
    </Modal>
  )
}

StorefrontSaveChanges.propTypes = {
  onClose: PropTypes.func.isRequired,
  trackers: PropTypes.func,
  redirectTo: PropTypes.string.isRequired,
}

export default StorefrontSaveChanges
